import React, { useState, useEffect } from "react";
import {
  Grid,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Divider,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  DialogTitle,
  DialogContentText,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { getCustomerByIdService } from "../../services/customerService";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from "axios";
import { API_URL } from "../../utils/apiUtils";
import { toast } from "react-toastify";

const getContactLabel = (contactType) => {
  switch (contactType) {
    case "phone":
      return "Phone Number";
    case "atl1":
      return "Alternate Phone Number 1";
    case "atl2":
      return "Alternate Phone Number 2";
    case "email":
      return "Email";
    default:
      return "Unknown Contact Type";
  }
};

const CustomerDetails = () => {
  const { id } = useParams(); // Get the customer ID from the URL
  const [customer, setCustomer] = useState(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openImagePreview, setOpenImagePreview] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [open, setOpen] = useState(false);

  const handleBackClick = () => {
    navigate(-1); // Navigate to the previous page
  };

  const fetchCustomerDetails = async () => {
    try {
      const response = await getCustomerByIdService(id);
      setCustomer(response);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomerDetails();
  }, [id]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setOpenImagePreview(true);
  };

  const handleClose = () => {
    setOpenImagePreview(false);
    setSelectedImage("");
  };

  const handleOpen = () => setOpen(true);
  const handleReverifyClose = () => setOpen(false);

  const handleReverifyConfirm = async () => {
    try {
      const token = sessionStorage.getItem("accessToken");
      const response = await axios.put(
        `${API_URL}/customer/update-reverification`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            id: id,
          },
        }
      );
      console.log(response);
      if (response.status === 200) {
        toast.success("Customer updated to reverify");
      }
      navigate("/customer");
      console.log("Re-verified!");
      handleReverifyClose();
    } catch (error) {
      toast.error(error);
    }
  };

  return (
    <Paper sx={{ padding: 4 }}>
      {customer && (
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Box sx={{ display: "flex" }}>
              {" "}
              <ArrowBackIcon
                onClick={handleBackClick}
                style={{ cursor: "pointer" }}
              />
              <Typography variant="h6" sx={{ color: "#5252FF", ml: 1 }}>
                Customer Information
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Button variant="contained" onClick={handleOpen}>
              Re Verify
            </Button>
          </Grid>
          {/* User Information */}
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Name:</strong> {customer.user.userName}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Father Name:</strong> {customer?.documents?.fatherName}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Customer Photo:</strong>{" "}
            </Typography>
            <img
              alt={customer?.user?.userName}
              src={customer?.user?.userPhoto || "/default-image.png"}
              style={{ width: 100, height: 100 }}
              onClick={() =>
                handleImageClick(
                  customer?.user?.userPhoto || "/default-image.png"
                )
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Created By :</strong> {customer.createdBy}
            </Typography>
          </Grid>
          {customer?.verifiedBy.length > 0 ? (
            <>
              {" "}
              <Grid item xs={12} md={6}>
                <Typography>
                  <strong>Verified By :</strong> {customer.verifiedBy}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography>
                  <strong>Verified Date :</strong> {customer.verifiedTime}
                </Typography>
              </Grid>
            </>
          ) : (
            <></>
          )}

          <Grid item xs={12}>
            <Divider />
          </Grid>
          {/* Contact Information */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ color: "#5252FF" }}>
              Contact Information
            </Typography>
          </Grid>
          {customer.contacts.map((contact) => (
            <Grid item xs={12} md={6} key={contact._id}>
              <Typography>
                <strong>{getContactLabel(contact.contactType)}:</strong>{" "}
                {contact.contactValue}
              </Typography>
            </Grid>
          ))}
          <Grid item xs={12}>
            <Divider />
          </Grid>
          {/* Address Information */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ color: "#5252FF" }}>
              Address Information
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Address:</strong> {customer.address?.address || "N/A"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Permanent Address:</strong>{" "}
              {customer.address?.permanentAddress || "N/A"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>City:</strong> {customer.address?.city || "N/A"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Pincode :</strong> {customer.address?.pincode || "N/A"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Landmark:</strong> {customer.address?.landmark || "N/A"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Location Link:</strong>{" "}
              <a href={customer.address?.locationLink}>View Location</a>
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>House Photo:</strong>{" "}
            </Typography>
            <img
              alt="House"
              src={customer?.address?.housePhoto || "/default-document.png"}
              style={{ width: 100, height: 100 }}
              onClick={() =>
                handleImageClick(
                  customer?.address?.housePhoto || "/default-image.png"
                )
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Landmark Photo:</strong>{" "}
            </Typography>
            <img
              alt="House"
              src={customer?.address?.housePhoto || "/default-document.png"}
              style={{ width: 100, height: 100 }}
              onClick={() =>
                handleImageClick(
                  customer?.address?.landmark1Photo || "/default-image.png"
                )
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Landmark Photo:</strong>{" "}
            </Typography>
            <img
              alt="House"
              src={customer?.address?.housePhoto || "/default-document.png"}
              style={{ width: 100, height: 100 }}
              onClick={() =>
                handleImageClick(
                  customer?.address?.landmark2Photo || "/default-image.png"
                )
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Building Photos:</strong>
            </Typography>
            {customer?.address?.buildingImages?.length > 0 ? (
              customer.address.buildingImages.map((img, index) => (
                <img
                  key={index}
                  alt={`Building ${index + 1}`}
                  src={img}
                  style={{ width: 100, height: 100, marginRight: 5 }}
                  onClick={() => handleImageClick(img)}
                />
              ))
            ) : (
              <img
                alt="Default Property"
                src="/default-image.png"
                style={{ width: 100, height: 100 }}
              />
            )}
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>
          {/* Document Information */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ color: "#5252FF" }}>
              Documents
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Loan Issued On:</strong>{" "}
              {customer.documents?.loanIssuedOn || "N/A"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Aadhaar Number:</strong>{" "}
              {customer?.documents?.aadhaarNumber || "N/A"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Aadhaar Photo:</strong>{" "}
            </Typography>
            <img
              alt="Aadhaar"
              src={customer.documents?.aadhaarPhoto || "/default-document.png"}
              style={{ width: 100, height: 100 }}
              onClick={() =>
                handleImageClick(
                  customer?.documents?.aadhaarPhoto || "/default-image.png"
                )
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>PAN Number:</strong>{" "}
              {customer.documents?.panNumber || "N/A"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>PAN Photo:</strong>{" "}
            </Typography>
            <img
              alt="PAN"
              src={customer.documents?.panPhoto || "/default-document.png"}
              style={{ width: 100, height: 100 }}
              onClick={() =>
                handleImageClick(
                  customer?.documents?.panPhoto || "/default-image.png"
                )
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Property Photo:</strong>{" "}
            </Typography>
            <img
              alt="Property"
              src={customer.documents?.propertyPhoto || "/default-document.png"}
              style={{ width: 100, height: 100 }}
              onClick={() =>
                handleImageClick(
                  customer?.documents?.propertyPhoto || "/default-image.png"
                )
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Property Holder Name:</strong>{" "}
              {customer.documents?.propertyHolderName || "N/A"}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography>
              <strong>RC Holder Name:</strong>{" "}
              {customer?.documents?.rcHolderName || "N/A"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>RC Photo:</strong>{" "}
            </Typography>
            <img
              alt="Driving Licence"
              src={customer.documents?.rcPhoto || "/default-document.png"}
              style={{ width: 100, height: 100 }}
              onClick={() =>
                handleImageClick(
                  customer?.documents?.rcPhoto || "/default-image.png"
                )
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Cheque Number:</strong>{" "}
              {customer.documents?.chequeNumber || "N/A"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Cheque Photo:</strong>{" "}
            </Typography>
            <img
              alt="Cheque"
              src={customer.documents?.chequePhoto || "/default-document.png"}
              style={{ width: 100, height: 100 }}
              onClick={() =>
                handleImageClick(
                  customer?.documents?.chequePhoto || "/default-image.png"
                )
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Signature Photo:</strong>{" "}
            </Typography>
            <img
              alt="Signature"
              src={
                customer.documents?.signatureImage || "/default-document.png"
              }
              style={{ width: 100, height: 100 }}
              onClick={() =>
                handleImageClick(
                  customer?.documents?.signatureImage || "/default-image.png"
                )
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" sx={{ color: "#5252FF" }}>
              Bank Information
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Pass Book Image:</strong>{" "}
            </Typography>
            <img
              alt="Pass Book"
              src={customer.documents?.passBookImage || "/default-document.png"}
              style={{ width: 100, height: 100 }}
              onClick={() =>
                handleImageClick(
                  customer?.documents?.passBookImage || "/default-image.png"
                )
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong> Account Number:</strong>{" "}
              {customer?.documents?.accNumber || "N/A"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Bank Name:</strong>{" "}
              {customer?.documents?.bankName || "N/A"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>IFSC:</strong>{" "}
              {customer?.documents?.bankIFSCCode || "N/A"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography>
              <strong>Branch:</strong>{" "}
              {customer?.documents?.bankBranchName || "N/A"}
            </Typography>
          </Grid>

          {customer?.nominee && (
            <>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ color: "#5252FF" }}>
                  Nominee Information
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography>
                  <strong>Nominee Name:</strong>{" "}
                  {customer.nominee.nomineeName || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography>
                  <strong>Nominee Phone Number:</strong>{" "}
                  {customer.nominee.nomineePhoneNumber || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography>
                  <strong>Nominee Aadhaar:</strong>{" "}
                  {customer.nominee.nomineeAadhaarNumber || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography>
                  <strong>Nominee Pan Number:</strong>{" "}
                  {customer.nominee.nomineePanNumber || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography>
                  <strong>Nominee Account Number:</strong>{" "}
                  {customer.nominee.nomineeAccountNumber || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography>
                  <strong>Nominee Account Name:</strong>{" "}
                  {customer.nominee.nomineeAccountName || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography>
                  <strong>Nominee IFSC:</strong>{" "}
                  {customer.nominee.nomineeIFSC || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography>
                  <strong>Nominee Branch:</strong>{" "}
                  {customer.nominee.nomineeBranch || "N/A"}
                </Typography>
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <Divider />
          </Grid>
          {/* Guarantor Information */}
          {customer.guarantorDetails && (
            <>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ color: "#5252FF" }}>
                  Guarantor Information
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography>
                  <strong>Name:</strong>{" "}
                  {customer.guarantorDetails.guarantor?.guarantorName || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography>
                  <strong>Guarantor Photo:</strong>{" "}
                </Typography>
                <img
                  alt={customer.guarantorDetails.guarantor?.guarantorName}
                  src={
                    customer.guarantorDetails.guarantor?.guarantorPhoto ||
                    "/default-image.png"
                  }
                  style={{ width: 100, height: 100 }}
                  onClick={() =>
                    handleImageClick(
                      customer.guarantorDetails.guarantor?.guarantorPhoto ||
                        "/default-image.png"
                    )
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography>
                  <strong>Phone:</strong>{" "}
                  {customer.guarantorDetails.guarantor?.guarantorPhoneNumber ||
                    "N/A"}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider
                  sx={{
                    my: 1,
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ color: "#5252FF" }}>
                  Guarantor Address
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography>
                  <strong>Address:</strong>{" "}
                  {customer.guarantorDetails.guarantorAddress?.address || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography>
                  <strong>Landmark:</strong>{" "}
                  {customer.guarantorDetails.guarantorAddress?.landmark ||
                    "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography>
                  <strong>Location Link:</strong>{" "}
                  <a
                    href={
                      customer.guarantorDetails.guarantorAddress?.locationLink
                    }
                  >
                    View Location
                  </a>
                </Typography>
              </Grid>
              {/* <Grid item xs={12} md={6}>
                <Typography>
                  <strong>House Photo:</strong>{" "}
                </Typography>
                <img
                  alt="House"
                  src={
                    customer.guarantorDetails.guarantorAddress?.housePhoto ||
                    "/default-document.png"
                  }
                  style={{ width: 100, height: 100 }}
                  onClick={() =>
                    handleImageClick(
                      customer.guarantorDetails.guarantorAddress?.housePhoto ||
                        "/default-image.png"
                    )
                  }
                />
              </Grid> */}

              <Grid item xs={12}>
                <Divider
                  sx={{
                    my: 1,
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ color: "#5252FF" }}>
                  Guarantor Documents
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography>
                  <strong>Aadhaar Number:</strong>{" "}
                  {customer.guarantorDetails.guarantorDocuments
                    ?.aadhaarNumber || "N/A"}
                </Typography>
              </Grid>
              {/* <Grid item xs={12} md={6}>
                <img
                  alt="Aadhaar"
                  src={
                    customer.guarantorDetails.guarantorDocuments
                      ?.aadhaarPhoto || "/default-document.png"
                  }
                  style={{ width: 100, height: 100 }}
                  onClick={() =>
                    handleImageClick(
                      customer.guarantorDetails.guarantorDocuments
                        ?.aadhaarPhoto || "/default-image.png"
                    )
                  }
                />
              </Grid> */}
              <Grid item xs={12} md={6}>
                <Typography>
                  <strong>Pan Number:</strong>{" "}
                  {customer.guarantorDetails.guarantorDocuments?.panNumber ||
                    "N/A"}
                </Typography>
              </Grid>
              {/* <Grid item xs={12} md={6}>
                <img
                  alt="Pan"
                  src={
                    customer.guarantorDetails.guarantorDocuments?.panPhoto ||
                    "/default-document.png"
                  }
                  style={{ width: 100, height: 100 }}
                  onClick={() =>
                    handleImageClick(
                      customer.guarantorDetails.guarantorDocuments?.panPhoto ||
                        "/default-image.png"
                    )
                  }
                />
              </Grid> */}
            </>
          )}
        </Grid>
      )}
      <Dialog open={openImagePreview} onClose={handleClose} maxWidth="md">
        <DialogContent>
          <img
            src={selectedImage}
            alt="Preview"
            style={{ width: "100%", height: "auto" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Confirm Re-verification</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to reverify this?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReverifyClose} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleReverifyConfirm}
            variant="contained"
            color="primary"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default CustomerDetails;
